import { Button, Paper, Typography } from "@mui/material";
import { classes } from "common/theme/theme";
import { JobType } from "protos/atk/job_type";
import { MuiIcon } from "common/components/types";
import { neighboringStep, NewJobsFormState } from "./FormState";
import { titleCase } from "common/utils/strings";
import { typedPath } from "typed-path";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import InSeasonIcon from "@mui/icons-material/GrassOutlined";
import LaserweedingIcon from "portal/images/icons/slayer_side_outlined.svg?react";
import React from "react";
import TractorIcon from "@mui/icons-material/AgricultureOutlined";
import TransportIcon from "@mui/icons-material/Route";

const S = typedPath<NewJobsFormState>();

interface JobTypeButtonProps {
  Icon: MuiIcon;
  headline: string;
  description: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  warning?: string;
}

const JobTypeButton: React.FC<JobTypeButtonProps> = ({
  Icon,
  headline,
  description,
  onClick,
  disabled = false,
  warning,
}) => {
  return (
    <Button
      variant="contained"
      color="inherit"
      className="flex flex-col gap-3 items-center p-6 rounded-lg cursor-pointer"
      // make the button stop yelling
      style={{ textTransform: "none" }}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="w-11 h-11 p-2 rounded-full bg-lighten-200" />
      <Typography variant="h6">{headline}</Typography>
      <Typography>{description}</Typography>
      {warning && (
        <Typography variant="body2" color="error">
          {warning}
        </Typography>
      )}
    </Button>
  );
};

export const JobTypeForm: React.FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<NewJobsFormState>();
  const { user } = useSelf();
  // in-season jobs are still experimental, so only Carbon employees can create them
  const showInSeason = user?.isInternal ?? false;

  const selectJobType = (jobType: JobType): void => {
    const nextStep = neighboringStep({ ...values, jobType }, "next");

    // each job type selects different kinds of zones
    if (jobType !== values.jobType) {
      setFieldValue(S.selectedFieldIds.$path, []);
    }
    setFieldValue(S.jobType.$path, jobType);
    setFieldValue(S.activeStep.$path, nextStep);
    setFieldTouched(S.jobType.$path, true);
    setFieldTouched(S.activeStep.$path, true);
  };

  return (
    <Paper
      variant="outlined"
      className="bg-inherit p-4 flex flex-col gap-4 rounded-lg"
    >
      <Typography variant="h5" className="col-span-full">
        {titleCase(t("models.autotractor.jobType"))}
      </Typography>
      <Typography color="textSecondary" className="col-span-full">
        {t("views.autotractor.jobCreation.step1JobType.description")}
      </Typography>
      <div
        className={classes("grid gap-5 grid-flow-row", {
          "grid-cols-4": showInSeason,
          "grid-cols-3": !showInSeason,
        })}
      >
        <JobTypeButton
          Icon={TractorIcon}
          headline={t(
            "views.autotractor.jobCreation.step1JobType.groundPrepName"
          )}
          description={t(
            "views.autotractor.jobCreation.step1JobType.groundPrepDescription"
          )}
          onClick={() => selectJobType(JobType.GROUND_PREP)}
        />
        <JobTypeButton
          Icon={LaserweedingIcon}
          headline={t(
            "views.autotractor.jobCreation.step1JobType.laserWeedingName"
          )}
          description={t(
            "views.autotractor.jobCreation.step1JobType.laserWeedingDescription"
          )}
          onClick={() => selectJobType(JobType.LASER_WEED)}
        />
        <JobTypeButton
          Icon={TransportIcon}
          headline={t(
            "views.autotractor.jobCreation.step1JobType.transportName"
          )}
          description={t(
            "views.autotractor.jobCreation.step1JobType.transportDescription"
          )}
          onClick={() => selectJobType(JobType.TRANSPORT)}
        />
        {showInSeason && (
          <JobTypeButton
            Icon={InSeasonIcon}
            headline={t(
              "views.autotractor.jobCreation.step1JobType.inSeasonName"
            )}
            description={t(
              "views.autotractor.jobCreation.step1JobType.inSeasonDescription"
            )}
            // not localized because this string is only visible to carbon users
            warning="Experimental - for internal Carbon Robotics use only"
            onClick={() => selectJobType(JobType.IN_SEASON)}
          />
        )}
      </div>
    </Paper>
  );
};
